import * as i0 from '@angular/core';
import { InjectionToken, Injectable, NgModule, Inject } from '@angular/core';
import * as i1 from '@angular/router';
import { ActivationEnd } from '@angular/router';
import { Subscription, filter, delay } from 'rxjs';
import { detect } from 'detect-browser';

const TELEMETRY_PROVIDER_TOKENS = new InjectionToken('TELEMETRY_PROVIDER_TOKENS');

const getBrowserAndPlatformProperties = () => {
    const browserAndPlatformProperties = detect();
    if (!browserAndPlatformProperties) {
        return {
            name: undefined,
            version: undefined,
            os: undefined,
            type: undefined,
        };
    }
    return browserAndPlatformProperties;
};
const getCommonProperties = () => {
    const { name: browser, version: browserVersion, os, type, } = getBrowserAndPlatformProperties();
    return {
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        network: {
            // this is needed as typescript doesn't have proper `navigator` types support
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            type: navigator?.connection?.effectiveType ?? undefined,
        },
        screen_height: window.screen.height,
        screen_width: window.screen.width,
        os,
        type,
        browser,
        browserVersion,
    };
};

class TelemetryService {
}

class TelemetryImplService extends TelemetryService {
    constructor(injector, router, activeRoute) {
        super();
        this.injector = injector;
        this.router = router;
        this.activeRoute = activeRoute;
        this.allTelemetryProviders = [];
        this._routeListener = new Subscription();
        this.setupAutomaticPageTracking();
    }
    initialize() {
        this.allTelemetryProviders.forEach((provider) => {
            provider.telemetryProvider.initialize(provider.initConfig);
        });
    }
    identify(userTraits) {
        this.allTelemetryProviders.forEach((provider) => {
            provider.telemetryProvider.identify(userTraits);
        });
        console.log({ userTraits });
    }
    trackEvent(eventMetadata) {
        const payload = {
            ...eventMetadata,
            commonProperties: getCommonProperties(),
            ...this.activeRoute.snapshot.queryParams,
        };
        this.allTelemetryProviders
            .filter((provider) => provider.enableEventTracking)
            .forEach((eventTrackingEnabledProvider) => {
            eventTrackingEnabledProvider.telemetryProvider.trackEvent?.(payload);
        });
        console.log({ payload });
    }
    trackPage(eventMetadata) {
        const payload = {
            ...eventMetadata,
            eventType: 'page_viewed',
            commonProperties: getCommonProperties(),
        };
        this.allTelemetryProviders
            .filter((provider) => provider.enablePageTracking)
            .forEach((eventTrackingEnabledProvider) => {
            eventTrackingEnabledProvider.telemetryProvider.trackPage?.(payload);
        });
        console.log({ payload });
    }
    shutdown() {
        this.allTelemetryProviders.forEach((provider) => {
            provider.telemetryProvider.shutdown?.();
        });
        this.allTelemetryProviders = [];
    }
    trackCustomEvent(eventType, eventMetadata) {
        this.trackEvent({
            eventType: eventType,
            ...eventMetadata,
        });
    }
    register(configs) {
        try {
            this.allTelemetryProviders = [
                ...this.allTelemetryProviders,
                ...configs.map((config) => this.buildTelemetryProvider(config)),
            ];
        }
        catch (error) {
            // TODO [2023-08-01]: Integrate with error service in future
            console.error({ error });
        }
    }
    buildTelemetryProvider(config) {
        const providerInstance = this.injector.get(config.telemetryProvider);
        return {
            ...config,
            telemetryProvider: providerInstance,
        };
    }
    setupAutomaticPageTracking() {
        this._routeListener.add(this.router?.events
            .pipe(filter((event) => event instanceof ActivationEnd), delay(50))
            .subscribe((event) => {
            const { enabled: analyticsEnabled, validator, pageInformationOverrideFunction, pageInformation, } = event.snapshot.data['analyticsConfig'] ?? {};
            const overridenPageInfo = pageInformationOverrideFunction
                ? pageInformationOverrideFunction.call(null, event.snapshot) ?? null
                : null;
            if (analyticsEnabled) {
                if ((validator && validator(event.snapshot)) || !validator) {
                    this.trackPage({
                        pageInformation,
                        ...overridenPageInfo,
                        ...event.snapshot.queryParams,
                    });
                }
            }
            return;
        }));
    }
    ngOnDestroy() {
        this.shutdown();
        this._routeListener.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TelemetryImplService, deps: [{ token: i0.Injector }, { token: i1.Router }, { token: i1.ActivatedRoute }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TelemetryImplService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TelemetryImplService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i0.Injector }, { type: i1.Router }, { type: i1.ActivatedRoute }] });

class TelemetryModule {
    constructor(providerConfigs, telemetryService) {
        telemetryService.register(providerConfigs);
    }
    static forRoot(providerConfigs) {
        return {
            ngModule: TelemetryModule,
            providers: [
                {
                    provide: TelemetryService,
                    useExisting: TelemetryImplService,
                },
                {
                    provide: TELEMETRY_PROVIDER_TOKENS,
                    useValue: providerConfigs,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TelemetryModule, deps: [{ token: TELEMETRY_PROVIDER_TOKENS }, { token: TelemetryImplService }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.8", ngImport: i0, type: TelemetryModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TelemetryModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: TelemetryModule, decorators: [{
            type: NgModule
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [TELEMETRY_PROVIDER_TOKENS]
                }] }, { type: TelemetryImplService }] });

/**
 * Generated bundle index. Do not edit.
 */

export { TELEMETRY_PROVIDER_TOKENS, TelemetryImplService, TelemetryModule, TelemetryService };
